/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: Photo 组件（纯展示，共享给 12+ 场景）
 *
 * 0925版本更新重点说明：
 * - sourceType判断，如果有sourceTypeDetail的话就用sourceTypeDetail，否则就是sourceType
 *
 * 标签（chip overlay / + 按钮 / 批量打标）UI 已迁移到 PhotoWithTag（review #1）。
 * 本组件保持纯展示语义，非打标场景（分类文件夹/加水印/选照片器）继续使用本组件。
 * 仅暴露 2 个最小 slot prop:
 *   - renderInPhoto?: 在 .photo image 内部追加渲染（chip overlay / + 按钮 slot 入口）
 *   - batchMode?:     通用 UI 模式（checkbox 常驻 + magnifier 隐藏），不与"打标"语义绑定
 * 共享场景不传这两个 prop = 行为完全跟旧版一样。
 */
import React, { useMemo, useState } from 'react'
import { Checkbox, Tooltip } from 'antd'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import CONFIG from '../../../constants/config'
import icon_play from '../../../images/icon_play.png'
import cheat_icon from '../../../images/cheat_icon.png'
import cheat_icon2 from '../../../images/cheat_icon2.png'
import './index.scss'
import {
  getSensorContent,
  getAbnormalPhotoTagInfo,
  getIn
} from '../../../utils/photoUtils'
import { useAppSelector, useVip, usePayment } from '../../../app/hooks'
import sensors, { sensorsRole } from '../../../utils/sensors'
import ContactUS from '../ContactUS'
import { ZoomInOutlined } from '@ant-design/icons'
// 智检入口已下线（master 70a77014, 2026-05-19，明阳智能大屏新版原型不再使用）

interface IPhotoComponent {
  style?: object
  photo: any
  canCheck?: boolean
  checked?: boolean
  readyToCheck?: boolean
  onPreview?: any
  onCheck?: any
  onUnCheck?: any
  track?: any
  onAbnormalClick?: any
  /** 通用 UI 模式：checkbox 常驻（不依赖 hover/readyToCheck）+ 顶部预览 magnifier 隐藏。
   *  跟"批量打标"语义解耦 — Photo 不知道为什么 batch，只看到外部传 batchMode=true。
   *  PhotoWithTag 在标签批量模式下传 true；共享场景不传 = false = 旧行为。 */
  batchMode?: boolean
  /** 在 .photo image 内部追加渲染的 slot.
   *  PhotoWithTag 用于注入 chip overlay + PhotoCardTagOverlay（+ 按钮）；
   *  共享场景不传 = 纯展示，跟旧版完全一致。 */
  renderInPhoto?: () => React.ReactNode
}

const RectificationTag = ({ photo }) => {
  const photoRectification = getIn(photo, ['photoRectification'])
  if (!photoRectification) {
    return null
  }
  const rectificationData = photoRectification
  const hasRectification = rectificationData && rectificationData.exist
  if (!hasRectification) {
    return null
  }
  return <div className="rectification-tag">有整改</div>
}

// 修改AbnormalPhotoTag组件，直接接收sourceType参数
const AbnormalPhotoTag = ({
  photo,
  onAbnormalClick,
  sourceType // 0925版本更新：直接接收计算好的sourceType值
}) => {
  const tagInfo = getAbnormalPhotoTagInfo(sourceType)

  // 获取Redux数据用于埋点
  const myRole = useAppSelector((state: any) => state.group.myRole)
  const groupId = useAppSelector((state: any) => state.group.groupId)

  // 埋点函数移到组件内部
  const handleSensorOfClickEvent = () => {
    sensors.track('workgroup_b4343_c7638_pageview', {
      string1: getIn(photo, ['seq']),
      string2: getIn(photo, ['userID']),
      number1: getIn(photo, ['time']),
      popType: getSensorContent(sourceType),
      role: sensorsRole(myRole),
      groupID: groupId || null,
      track_sign:
        'aworkgroup.b4343.c7638.pageview.workgroup_b4343_c7638_pageview',
      requirement_id: 276
    })
  }

  if (!tagInfo) return null

  return (
    <Tooltip
      title={tagInfo.text}
      classNames={{
        root: `abnormal-photo-tooltip abnormal-photo-tooltip-${tagInfo.type}`
      }}
      placement="bottom"
    >
      <div
        className={`abnormal-photo-tag ${tagInfo.type}`}
        onClick={(e) => {
          e.stopPropagation()
          onAbnormalClick && onAbnormalClick()
        }}
      >
        <img
          src={tagInfo.type === 'fake' ? cheat_icon2 : cheat_icon}
          alt={tagInfo.type === 'fake' ? '假' : '!'}
          onMouseEnter={handleSensorOfClickEvent}
        />
      </div>
    </Tooltip>
  )
}

const Photo = (props: IPhotoComponent) => {
  const {
    photo,
    canCheck,
    checked,
    readyToCheck,
    onPreview,
    onCheck,
    onUnCheck,
    track,
    onAbnormalClick,
    batchMode = false,
    renderInPhoto
  } = props

  const aiVisualSearchAB = useAppSelector(
    (state: any) => state.photo.aiVisualSearchAB
  )
  const { isVip, isSVip, isVVip } = useVip()
  const payment = usePayment()
  const [showCheckbox, setShowCheckbox] = useState(false)

  const isLimitViewByPhotoCount = !!getIn(photo, ['isLimitViewByPhotoCount'])
  const handlePhotoClick = isLimitViewByPhotoCount ? undefined : onPreview

  const photoTimeValue = getIn(photo, ['time'])
  const photoTime =
    photoTimeValue > 9999999999 ? photoTimeValue / 1000 : photoTimeValue

  // 0925版本更新：判断sourceTypeDetail是否存在，存在则使用，否则使用sourceType
  const sourceType = useMemo(() => {
    const sourceTypeDetail = getIn(photo, ['sourceTypeDetail'])
    return sourceTypeDetail !== undefined && sourceTypeDetail !== null
      ? sourceTypeDetail
      : getIn(photo, ['sourceType'])
  }, [photo])

  return (
    <div
      className="photo-component"
      style={props.style}
      onMouseEnter={() => {
        if (getIn(photo, ['mediaType']) !== 1 && aiVisualSearchAB && track) {
          track('AIPhotoIdentifyShow')
        }
        setShowCheckbox(true)
      }}
      onMouseLeave={() => setShowCheckbox(false)}
    >
      <div
        className="checkbox-wrapper"
        style={{
          // batchMode 时 checkbox 永远显示；否则按原逻辑（hover/已选/readyToCheck）
          display:
            batchMode || showCheckbox || checked || readyToCheck
              ? 'block'
              : 'none'
        }}
      >
        {canCheck && (
          <Checkbox
            className="check-box"
            checked={checked}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              if (e.target.checked) {
                onCheck(photo)
              } else {
                onUnCheck(photo)
              }
            }}
          />
        )}
      </div>
      <div className="photo-content">
        <div
          className="photo"
          onClick={handlePhotoClick}
          style={{
            position: 'relative'
          }}
        >
          <div
            className="photo-bg"
            style={{
              backgroundImage: `url(${getIn(photo, ['smallURL'])})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              // 当 isLimitViewByPhotoCount 为 true 时应用模糊效果
              filter: isLimitViewByPhotoCount ? 'blur(3px)' : undefined,
              zIndex: 1,
              pointerEvents: 'none'
            }}
          />
          {/* 播放图标、标签、异常标记放在模糊层之上 */}
          {getIn(photo, ['mediaType']) === 1 && (
            <img
              className="play-icon"
              src={icon_play}
              alt=""
              style={{ position: 'relative', zIndex: 5 }}
            />
          )}
          {!readyToCheck && <RectificationTag photo={photo} />}
          <AbnormalPhotoTag
            photo={photo}
            onAbnormalClick={onAbnormalClick}
            sourceType={sourceType} // 0925版本更新：传递计算好的sourceType值
          />
          {/* PhotoWithTag 通过此 slot 注入 chip overlay + PhotoCardTagOverlay。
              共享场景不传 = 不渲染额外内容，跟旧版完全一致。
              因为这里在 .photo（position:relative）内部，注入的绝对定位子元素正确 anchor 到 image。 */}
          {renderInPhoto && renderInPhoto()}
          {/* 如果存储超限，显示覆盖层文本（不被模糊且不可选中） */}
          {isLimitViewByPhotoCount && (
            <>
              {isSVip ? (
                <ContactUS
                  fromPage="photo_storage_value"
                  artificial={false}
                  style={{ display: 'block' }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: '#ffffff',
                      fontSize: 20,
                      fontWeight: 600,
                      zIndex: 20,
                      pointerEvents: 'auto',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      textShadow: '0 2px 4px rgba(0,0,0,0.6)',
                      cursor: 'pointer'
                    }}
                  >
                    空间已满
                  </div>
                </ContactUS>
              ) : (
                <div
                  onClick={(e) => {
                    e.stopPropagation()
                    if (isVVip) {
                      // 高级会员，引导开通超级会员（VVIP）
                      payment.openSvip(
                        'webGroupPhotoStorageVip',
                        false,
                        true,
                        false
                      )
                      return
                    }

                    if (isVip) {
                      // 普通会员，引导开通高级会员（SVIP）
                      payment.openSvip(
                        'webGroupPhotoStorageVip',
                        false,
                        false,
                        true
                      )
                      return
                    }

                    // 未开通会员，引导开通普通会员
                    payment.open('webGroupPhotoStorageVip')
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: '#ffffff',
                    fontSize: 20,
                    fontWeight: 600,
                    zIndex: 20,
                    pointerEvents: 'auto',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    textShadow: '0 2px 4px rgba(0,0,0,0.6)',
                    cursor: 'pointer'
                  }}
                >
                  空间已满
                </div>
              )}
            </>
          )}
        </div>
        <Tooltip title="点击查看个人照片">
          <p
            className="info"
            style={{ color: '#0070F4', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation()
              const userId = getIn(photo, ['userID'])
              if (userId) {
                track && track('clickUser')
                window.open(
                  `${window.location.pathname}?member_id=${userId}`,
                  '_blank'
                )
              }
            }}
          >
            {getIn(photo, ['userNickname']) || getIn(photo, ['nickname'])}
          </p>
        </Tooltip>
        <p className="info" onClick={(e) => e.stopPropagation()}>
          {dayjs.unix(photoTime).year() === dayjs().year()
            ? dayjs.unix(photoTime).format(CONFIG.photoTimeThisYearFormat)
            : dayjs.unix(photoTime).format(CONFIG.photoTimeFormat)}
        </p>
        <Tooltip title={getIn(photo, ['addr'])}>
          <p
            style={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis'
            }}
            className="info"
            onClick={(e) => e.stopPropagation()}
          >
            {getIn(photo, ['addr'])}
          </p>
        </Tooltip>
      </div>
      {/* batchMode 下不渲染 check-block 蓝框 + 放大镜：
          - 蓝框会让用户误以为该卡片"被选中"（实际只是 readyToCheck 状态）
          - batchMode 整卡片点击 = 切勾选，不需要单独的放大镜入口
          原 readyToCheck 蓝框是给"普通选择模式"（其他照片已选，这张未选）提示用的 */}
      {readyToCheck && !batchMode && (
        <div
          className="check-block"
          onClick={(e) => {
            if (checked) {
              onUnCheck(photo)
            } else {
              onCheck(photo)
            }
          }}
        >
          {getIn(photo, ['isDownloaded']) && (
            <div className="downloaded-tag">已下载</div>
          )}
          <div
            className="magnifierIcon"
            style={{
              position: 'absolute',
              left: 6,
              top: 10
            }}
            onClick={(e) => {
              e.stopPropagation()
              track && track('preview')
              onPreview()
            }}
          >
            <ZoomInOutlined style={{ fontSize: '20px' }} />
          </div>
        </div>
      )}
    </div>
  )
}

Photo.propTypes = {
  photo: PropTypes.object.isRequired,
  canCheck: PropTypes.bool,
  checked: PropTypes.bool,
  readyToCheck: PropTypes.bool,
  onPreview: PropTypes.func,
  onCheck: PropTypes.func,
  onUnCheck: PropTypes.func
}

export default Photo
